/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { Fragment } from 'react';
import { ClickAwayListener, Collapse, FormHelperText, Paper, Popper, TextField } from '@mui/material';
import { Z_INDEX } from './constants';
import LuiTree from './CustomLuiTree';
import { NodeItem } from './types';

interface ISingleSelectTreeProps {
  handleItemClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, selectedItem: NodeItem) => void;
  expandedItems: string[];
  textField: {
    id: string;
    name: string;
    label: string;
    value: string[];
  };
  tree: {
    selectedItems: string[];
    onSelectedItem: (selectedItem: any, tree: any[]) => void;
    data: any;
  };
  errorState: {
    error: boolean;
    helperText: string;
  };
  helperText: string;
}

const SingleSelectTree = ({
  textField,
  tree,
  errorState,
  handleItemClick,
  expandedItems,
  helperText
}: ISingleSelectTreeProps) => {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const ToggleTreeVisibility = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setOpen(prev => !prev);
  };

  return (
    <Fragment>
      <TextField
        id={textField.id}
        name={textField.name}
        label={textField.label}
        fullWidth
        variant="standard"
        multiline
        value={textField.value}
        onClick={ToggleTreeVisibility}
      />
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="top-end"
        transition
        style={{ width: '100%', maxWidth: '700px', zIndex: Z_INDEX }}
      >
        {({ TransitionProps }) => (
          <Collapse
            {...TransitionProps}
            style={{ width: '100%', maxWidth: '700px', zIndex: Z_INDEX }}
            timeout="auto"
            unmountOnExit
          >
            <ClickAwayListener onClickAway={ToggleTreeVisibility}>
              <Paper style={{ overflowX: 'auto', height: '350px' }}>
                <LuiTree
                  data={tree.data}
                  selectedItems={tree.selectedItems}
                  keyAccessor="key"
                  displayAccessor="value"
                  showSelection
                  onSelectItem={tree.onSelectedItem}
                  handleItemClick={handleItemClick}
                  expandedItems={expandedItems}
                />
              </Paper>
            </ClickAwayListener>
          </Collapse>
        )}
      </Popper>
      <FormHelperText>{helperText}</FormHelperText>
      <FormHelperText id={`${textField.id}-helper-text`} error={errorState.error}>
        {errorState.helperText}
      </FormHelperText>
    </Fragment>
  );
};

export default SingleSelectTree;
